import { useState, useEffect } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableContainer,
  TextField,
  TableCell,
  TableRow,
  IconButton,
  TableFooter,
} from "@mui/material";
import { format } from "date-fns";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import * as consts from "../constants";
import { useTranslation } from "react-i18next";
import { useNavigate, Navigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks/redux-hooks";
import {
    getDriverOrdersByMonth,
} from "../slices/authSlice";
import { getServiceTypeText } from '../utils/orderUtils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// 配置 dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

export function DriverHistoryView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const orders = useAppSelector((state) => state.auth.driver_orders);
  const driverInfo = useAppSelector((state) => state.auth.driverInfo);
  
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));

  useEffect(() => {
    if (driverInfo) {
      const formattedDate = format(selectedMonth, "yyyy-MM");
      dispatch(
        getDriverOrdersByMonth({ driver_id: +driverInfo.uid, month: formattedDate })
      );
    }
  }, [selectedMonth]);

  if (!driverInfo) {
    return <Navigate replace to={"/driver-login"} />;
  }

  const headerStyle = {
    backgroundColor: "#f5f5f5",
    fontWeight: 600,
    position: "sticky",
    left: 0,
    zIndex: 1,
    minWidth: "90px",
    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
  };

  const cellStyle = {
    padding: "16px",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    whiteSpace: "nowrap" as const,
  };

  const multiLineCellStyle = {
    ...cellStyle,
    whiteSpace: "pre-wrap",
    maxWidth: "200px",
  };

  const paymentTypeMap = {
    [consts.PAYMENT_TYPE.CREDIT_CARD]: t("creditCard"),
    [consts.PAYMENT_TYPE.CASH]: t("cash"),
    [consts.PAYMENT_TYPE.ZELLE]: "Zelle",
    [consts.PAYMENT_TYPE.VENMO]: "Venmo",
    [consts.PAYMENT_TYPE.ACCOUNT_BILLING]: t("accountBilling"),
    [consts.PAYMENT_TYPE.CHECK]: t("labelCheck"),
  };

  // 计算汇总数据
  const summary = orders.reduce(
    (acc, order) => ({
      total_base_fare: Number(acc.total_base_fare) + Number(order.base_fare || 0),
      total_tips: Number(acc.total_tips) + Number(order.tips || 0),
    }),
    { total_base_fare: 0, total_tips: 0 }
  );

  return (
    <>
      <Box sx={{ mb: 5 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            p: 2,
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            justifyContent: "space-between",
          }}
        >
          {/* <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box
            sx={{
              typography: "h6",
              fontWeight: "bold",
              mr: 2,
            }}
          >
            {t("labelOrderList")}
          </Box>
          <TextField
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            sx={{ width: 200 }}
          /> */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <IconButton onClick={() => navigate(-1)}>
        <ArrowBackIcon />
      </IconButton>
      <Box
        sx={{
          typography: "h6",
          fontWeight: "bold",
        }}
      >
        {t("labelOrderList")}
      </Box>
    </Box>

    <TextField
      type="month"
      value={selectedMonth}
      onChange={(e) => setSelectedMonth(e.target.value)}
      sx={{ width: 200 }}
    />

        </Box>
      </Box>

      <Card>
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            <Table
              sx={{
                borderCollapse: "separate",
                borderSpacing: "0",
                "& tr:hover td": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
                "& tr:hover td:first-of-type": {
                  backgroundColor: "#eeeeee",
                },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell sx={headerStyle}>{t("orderId")}</TableCell>
                  <TableCell sx={headerStyle}>{t("status")}</TableCell>
                  <TableCell sx={headerStyle}>{t("serviceType")}</TableCell>
                  <TableCell sx={headerStyle}>{t("name")}</TableCell>
                  <TableCell sx={headerStyle}>{t("useTime")}</TableCell>
                  <TableCell sx={headerStyle}>{t("flightTime")}</TableCell>
                  <TableCell sx={headerStyle}>{t("airport")}</TableCell>
                  <TableCell sx={headerStyle}>{t("labelAddress")}1</TableCell>
                  <TableCell sx={headerStyle}>{t("labelBaseFarePaymentWay")}</TableCell>
                  <TableCell sx={headerStyle}>{t("baseFare")}</TableCell>
                  <TableCell sx={headerStyle}>{t("labelTipsPaymentWay")}</TableCell>
                  <TableCell sx={headerStyle}>{t("tips")}</TableCell>
                </TableRow>
                {orders.map((order) => (
                  <TableRow key={order.order_id}>
                    <TableCell sx={cellStyle}>{order.order_id}</TableCell>
                    <TableCell sx={cellStyle}>
                      {order.status === consts.orderStatus.ASSIGNED
                        ? t("assigned")
                        : t("completed")}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {getServiceTypeText(order.service_type, t)}
                    </TableCell>
                    <TableCell sx={cellStyle}>{order.customer_name}</TableCell>
                    {/* <TableCell sx={cellStyle}>
                      {order.use_time
                        ? format(new Date(order.use_time), "yyyy-MM-dd HH:mm")
                        : "--"}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {order.flight_time
                        ? format(new Date(order.flight_time), "yyyy-MM-dd HH:mm")
                        : "--"}
                    </TableCell> */}
                    <TableCell sx={cellStyle}>
                      {order.use_time
                        ? dayjs(order.use_time).tz('America/Los_Angeles').format('YYYY-MM-DD HH:mm') + ' PST'
                        : "--"}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {order.flight_time
                        ? dayjs(order.flight_time).tz('America/Los_Angeles').format('YYYY-MM-DD HH:mm') + ' PST'
                        : "--"}
                    </TableCell>
                    <TableCell sx={cellStyle}>{order.airport || "--"}</TableCell>
                    <TableCell sx={multiLineCellStyle}>{order.addr1 || "--"}</TableCell>
                    <TableCell sx={cellStyle}>
                      {order.base_fare_payment_method
                        ? paymentTypeMap[order.base_fare_payment_method]
                        : "--"}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {order.base_fare == consts.wrongFare ? (
                        <Box sx={{ color: 'error.main', fontWeight: 'bold' }}>
                          车资错误
                        </Box>
                      ) : (
                        order.base_fare || "--"
                      )}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {order.tips_payment_method
                        ? paymentTypeMap[order.tips_payment_method]
                        : "--"}
                    </TableCell>
                    <TableCell sx={cellStyle}>{order.tips || "--"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {/* <TableFooter>
              <TableRow>
                <TableCell sx={headerStyle} colSpan={9}>
                    {t("labelTotal")}
                </TableCell>
                <TableCell sx={{ ...headerStyle, fontWeight: 'bold' }}>
                    {Number(summary.total_base_fare).toFixed(2)}
                </TableCell>
                <TableCell sx={headerStyle} />
                <TableCell sx={{ ...headerStyle, fontWeight: 'bold' }}>
                    {Number(summary.total_tips).toFixed(2)}
                </TableCell>
                </TableRow>
              </TableFooter> */}
            </Table>
          </TableContainer>
        </Box>
      </Card>
    </>
  );
}